import {
  ColDef,
  GridReadyEvent,
  ValueFormatterParams,
  IServerSideGetRowsParams,
  ICellRendererParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { RefObject } from "react";
import { useTranslation } from "react-i18next";
import {
  dateFilterModel,
  dateFilterParams,
} from "../../../../utils/Formatter/AgGridFilter";
import { formatDate } from "../../../../utils/Date";
import AgGrid from "../../../UI/AgGrid";
import { GraphQLClient } from "graphql-request";
import { createGraphQLClientWithMiddleware } from "../../../../services/graphqlClient";
import { UNSCANNED_SN_REPORT_VIEW } from "../../../../services/AgGrid/Report/InventoryReportAggrid";
import CustomizedStatus from "../../../Custom/CustomizedStatus";
import { statusValueFormatter } from "../../../../utils/Formatter/Global";

interface Props {
  gridRef: RefObject<AgGridReact>;
}

const UnscannedSNReportTable = ({ gridRef }: Props) => {
  const { t } = useTranslation();

  const columnDefs: ColDef[] = [
    {
      field: "created_date",
      headerName: t("date.created_date"),
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
    },
    {
      field: "serial_no",
      headerName: "Batch/SN",
      filter: "agTextColumnFilter",
    },
    {
      field: "main_category",
      headerName: t("inventory.items.main_category"),
      filter: "agNumberColumnFilter",
    },
    {
      field: "item_name",
      headerName: "รหัสสินค้า-ชื่อสินค้า",
      filter: "agTextColumnFilter",
    },
    {
      field: "qty",
      headerName: t("inventory.list.qty"),
      filter: "agNumberColumnFilter",
      valueFormatter: (params) =>
        params.value ? params.value.toLocaleString() : 0,
      cellStyle: { display: "flex", justifyContent: "flex-end" },
    },
    {
      field: "uom",
      headerName: t("inventory.unit"),
      filter: "agTextColumnFilter",
    },
    {
      field: "type",
      headerName: t("inventory.stockEntry.entryDocumentType"),
      filter: "agSetColumnFilter",
      filterParams: {
        values: [
          "goods_receive",
          "goods_return",
          "goods_issue",
          "goods_transfer",
          "goods_adjust",
        ],
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === "goods_adjust")
            return t("inventory.goods_adjustment.index");
          return t(`inventory.${params.value}.index`);
        },
      },
      valueFormatter: (params: ValueFormatterParams) => {
        if (params.value === "goods_adjust")
          return t("inventory.goods_adjustment.index");
        return t(`inventory.${params.value}.index`);
      },
    },
    {
      field: "branch_id",
      headerName: t("branch.unique_id"),
      filter: "agTextColumnFilter",
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      field: "branch_unique_id",
      headerName: t("branch.unique_id"),
      filter: "agTextColumnFilter",
    },
    {
      field: "branch_name",
      headerName: t("branch.index"),
      filter: "agTextColumnFilter",
    },
    {
      field: "warehouse_id",
      headerName: t("inventory.warehouseId"),
      filter: "agTextColumnFilter",
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      field: "warehouse_unique_id",
      headerName: t("inventory.warehouseId"),
      filter: "agTextColumnFilter",
      hide: true,
    },
    {
      field: "warehouse_name",
      headerName: t("inventory.warehouse"),
      filter: "agTextColumnFilter",
    },
    {
      field: "barcode",
      headerName: "Barcode",
      filter: "agTextColumnFilter",
    },
    {
      field: "lot_date",
      headerName: t("reports.lot_date"),
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
    },
    {
      field: "manufactured_date",
      headerName: t("date.manufactured_date"),
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
    },
    {
      field: "expired_date",
      headerName: t("date.expired_date"),
      filter: "agDateColumnFilter",
      filterParams: dateFilterParams,
      valueFormatter: (params: ValueFormatterParams) =>
        formatDate(params.value),
    },
    {
      field: "reference_unique_id",
      headerName: t("sentence.reference_unique_id"),
      filter: "agTextColumnFilter",
    },
    {
      field: "aggrid_status",
      headerName: t("status.index") + "เอกสาร",
      filter: "agSetColumnFilter",
      cellRenderer: (params: ICellRendererParams) => (
        <CustomizedStatus status={params.value} />
      ),
      filterParams: {
        values: [
          "draft",
          "finished",
          "cancelled",
          "wait_approve",
          "not_approved",
          "wait_receive",
          "wait_issue",
          "wait_transfer",
          "in_transit",
        ],
        valueFormatter: (params: ValueFormatterParams) =>
          statusValueFormatter(params.value),
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    },
  ];

  const graphQLClient: GraphQLClient = createGraphQLClientWithMiddleware("wms");

  const datasource = {
    async getRows(params: IServerSideGetRowsParams) {
      const { request } = params;
      const { startRow, endRow, filterModel, sortModel } = request;

      const { lot_date, manufactured_date, expired_date, created_date } =
        filterModel;

      const formatFilter = {
        ...filterModel,
        created_date: dateFilterModel(created_date),
        lot_date: dateFilterModel(lot_date),
        manufactured_date: dateFilterModel(manufactured_date),
        expired_date: dateFilterModel(expired_date),
      };

      try {
        const { UnscannedSNReportView } = await graphQLClient.request(
          UNSCANNED_SN_REPORT_VIEW,
          {
            aggridInput: {
              startRow,
              endRow,
              filterModel: formatFilter,
              sortModel,
            },
          }
        );
        params.success({
          rowData: UnscannedSNReportView.results,
          rowCount: UnscannedSNReportView.count,
        });
      } catch (err) {
        params.fail();
      }
    },
  };

  const onGridReady = (params: GridReadyEvent) => {
    params.api.setServerSideDatasource(datasource);
  };

  return (
    <AgGrid
      height={665}
      ref={gridRef}
      columnDefs={columnDefs}
      onGridReady={onGridReady}
    />
  );
};

export default UnscannedSNReportTable;
